// src/utils/statusLabels.js
//
// Display-only label mapping. Raw Firestore status/name values (يوسف, برهان,
// حليمة, محمد, "عند يوسف", "عند برهان", ...) must stay unchanged in the
// database and in all comparison/routing logic — this file only converts
// those raw values into the labels shown to the user, per دورة حياة الطلب:
// عند المصمم -> عند المطبعة -> عند الورشة (اطار) -> عند الرسام -> عند بنين
// (المقارنة) -> عند الورشة (تغليف).
//
// `order` (2nd arg, optional) lets callers that have the full order object
// resolve finer sub-states (بدأ العمل / اطار / تغليف) that aren't encoded in
// the raw status string alone but derived from order.startTime /
// order.completedSteps. Without it, the function still returns a sensible
// (slightly less precise) label.

export function getDisplayStatus(rawStatus, order) {
  if (!rawStatus || typeof rawStatus !== "string") return rawStatus || "";

  let s = rawStatus;

  // ✅ عند المصمم: "بدأ العمل" يُعرف بوجود order.startTime، لا بقيمة status منفصلة
  if (s === "عند يوسف") {
    return order?.startTime ? "عند المصمم - بدأ العمل" : "عند المصمم";
  }

  // ✅ عند المطبعة: كل حالة مخزّنة فعلياً كنص منفصل
  if (s === "بدأ الطباعة") return "عند المطبعة - بدأ العمل";
  if (s === "اكتملت الطباعة") return "عند المطبعة - تمت الطباعة";
  // ✅ "طباعة" بدون بادئة: خطوة طباعة خارجية يسجّلها الورشة يدوياً (نفس معنى "اكتملت الطباعة")
  if (s === "طباعة") return "عند المطبعة - تمت الطباعة";

  // ✅ خطوات ورشة/مقارنة تُسجَّل بنص history خام بلا بادئة "عند برهان -"
  if (s === "رسم") return "عند الرسام"; // وصل الطلب للرسام
  if (s === "إطار") return "عند الورشة - اطار مكتمل"; // اكتملت خطوة الإطار
  if (s === "تمت الموافقة على المقارنة") return "عند بنين - تمت المقارنة";

  // compound رسم حليمة/محمد/جنات variants first (most specific -> least specific)
  // (جنات appears as a legacy illustrator-name variant in some older dashboards'
  // status-translation code, alongside حليمة/محمد, kept here for the same
  // backward-compatibility reason)
  s = s.replace("عند برهان - رسم حليمة بدأت", "عند الرسام - بدأ العمل");
  s = s.replace("عند برهان - رسم حليمة مكتمل", "عند الرسام - تم الرسم");
  s = s.replace("عند برهان - رسم محمد بدأت", "عند الرسام - بدأ العمل");
  s = s.replace("عند برهان - رسم محمد مكتمل", "عند الرسام - تم الرسم");
  s = s.replace("عند برهان - رسم جنات بدأت", "عند الرسام - بدأ العمل");
  s = s.replace("عند برهان - رسم جنات مكتمل", "عند الرسام - تم الرسم");
  s = s.replace("عند برهان - رسم حليمة", "عند الرسام");
  s = s.replace("عند برهان - رسم محمد", "عند الرسام");
  s = s.replace("عند برهان - رسم جنات", "عند الرسام");

  // ✅ بنين: انتظار المقارنة هو النص الوحيد المخزّن فعلياً لهذه الخطوة
  s = s.replace("عند برهان - بانتظار المقارنة", "عند بنين - بإنتظار المقارنة");

  // ✅ تغليف مباشر (بيع مباشر) يقع ضمن نفس خطوة "تغليف" بالورشة
  s = s.replace("عند برهان - تغليف مباشر", "عند الورشة - تم التغليف");

  // ✅ طباعة خارجية تُسجَّل بالورشة كخطوة "طباعة" (نفس معنى "اكتملت الطباعة")
  s = s.replace("عند برهان - طباعة", "عند المطبعة - تمت الطباعة");

  // ✅ عند الورشة (اطار/تغليف): لوحات "آخر خطوة مكتملة" تصل هنا كنص مركّب
  // "عند برهان - <step>" (يبنيه استدعاء renameStatus بالداشبوردات من
  // order.completedSteps، وليس بحالة status منفصلة بقاعدة البيانات).
  // آخر خطوة مكتملة = إطار → الخطوة الحالية صارت تغليف.
  s = s.replace("عند برهان - إطار", "عند الورشة - تغليف");
  s = s.replace("عند برهان - تغليف", "عند الورشة - تم التغليف");

  // ✅ الحالة الخام "عند برهان" بلا بادئة تتكرر بمرحلتين مختلفتين: أول وصول
  // للورشة (بانتظار الإطار) وبعد موافقة المقارنة (بانتظار التغليف). التمييز
  // يعتمد على completedSteps: تحتوي "إطار" فقط لو خطوة التأطير خلصت سابقاً.
  if (s === "عند برهان") {
    return order?.completedSteps?.includes("إطار")
      ? "عند الورشة - تغليف"
      : "عند الورشة - اطار";
  }

  // generic fallbacks
  s = s.replace("برهان", "الورشة");
  s = s.replace("يوسف", "المصمم");
  s = s.replace("حليمة", "الرسام");
  s = s.replace("محمد", "الرسام");
  s = s.replace("جنات", "الرسام");

  return s;
}

export function getDisplayName(rawName) {
  const map = {
    "يوسف": "المصمم",
    "حليمة": "الرسام",
    "محمد": "الرسام",
    "برهان": "الورشة",
  };

  return map[rawName] || rawName;
}
